import { useEffect, useState, useCallback, useRef } from "react";
import { useGlobalAPI } from "../../../../hooks/useGlobalAPI";

export function useTimelineDiagram(metric = "active_count", activeFile = null) {
  const api = useGlobalAPI(activeFile);
  const [objectTypes, setObjectTypes] = useState([]);
  const [timelineData, setTimelineData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [error, setError] = useState("");
  const [isMetadataLoaded, setIsMetadataLoaded] = useState(false);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [granularity, setGranularity] = useState("daily");
  const [objectType, setObjectType] = useState("");
  const [globalDateRange, setGlobalDateRange] = useState(null);

  const fetchObjectTypes = useCallback(async () => {
    setMetadataLoading(true);
    setError("");
    try {
      // Use original fast cached endpoint for metadata loading
      const response = await api.get('/object-types', {}, {}, 'Object Types');
      const allTypes = response.objectTypes || response || [];
      
      // Apply client-side filtering based on current global filters
      const activeFilters = api.currentFilters;
      let filteredTypes = allTypes;
      
      // Apply object type filter if specified
      if (activeFilters.objectTypes && activeFilters.objectTypes.length > 0) {
        filteredTypes = allTypes.filter(type => activeFilters.objectTypes.includes(type));
      }
      
      console.log("📊 Timeline metadata loaded:", { 
        allTypes: allTypes.length, 
        filteredTypes: filteredTypes.length,
        hasFilters: activeFilters.objectTypes && activeFilters.objectTypes.length > 0
      });
      
      setObjectTypes(filteredTypes);
      
      // Extract and set global date range if available
      if (activeFilters.dateRange && activeFilters.dateRange.enabled) {
        const globalRange = {
          startDate: activeFilters.dateRange.startDate,
          endDate: activeFilters.dateRange.endDate
        };
        setGlobalDateRange(globalRange);
        
        // Initialize timeline date inputs with global range if they're empty
        // Note: Using functional updates to avoid dependency issues
        setStartDate(current => !current && globalRange.startDate ? globalRange.startDate : current);
        setEndDate(current => !current && globalRange.endDate ? globalRange.endDate : current);
        
        console.log("📅 Global date range applied:", globalRange);
      } else {
        setGlobalDateRange(null);
      }
      
      setIsMetadataLoaded(true);
    } catch (err) {
      setError("Failed to load configuration options.");
      setObjectTypes([]);
      setIsMetadataLoaded(false);
    } finally {
      setMetadataLoading(false);
    }
  }, [api]);

  useEffect(() => {
    // Reset component state when the file changes to force re-initialization
    setObjectTypes([]);
    setTimelineData([]);
    setError("");
    setStartDate("");
    setEndDate("");
    setGranularity("daily");
    setObjectType("");
    setIsMetadataLoaded(false);
  }, [activeFile]);

  // Auto-reload metadata when global filters change (like WeeklyActivityPattern)
  // Use a ref to track filter changes and avoid infinite loops
  const prevFiltersRef = useRef();
  
  useEffect(() => {
    const currentFiltersStr = JSON.stringify(api.currentFilters);
    const prevFiltersStr = JSON.stringify(prevFiltersRef.current);
    
    if (isMetadataLoaded && currentFiltersStr !== prevFiltersStr) {
      console.log("📊 Global filters changed, reloading metadata for timeline");
      fetchObjectTypes();
    }
    
    prevFiltersRef.current = api.currentFilters;
  }, [api.currentFilters, isMetadataLoaded, fetchObjectTypes]);

  useEffect(() => {
    if (!startDate || !endDate || !objectType) {
      setTimelineData([]);
      return;
    }

    async function fetchData() {
      setLoading(true);
      setError("");
      
      try {
        // Check if filters are meaningful and should use the POST endpoint
        const activeFilters = api.currentFilters;
        const hasFilters = activeFilters && (
          (activeFilters.objectTypes && activeFilters.objectTypes.length > 0) ||
          (activeFilters.dateRange && activeFilters.dateRange.enabled && 
           (activeFilters.dateRange.startDate || activeFilters.dateRange.endDate)) ||
          (activeFilters.eventAttributeFilters && activeFilters.eventAttributeFilters.some(f => f.enabled)) ||
          (activeFilters.activityFilters && activeFilters.activityFilters.enabled && 
           ((activeFilters.activityFilters.included && activeFilters.activityFilters.included.length > 0) ||
            (activeFilters.activityFilters.excluded && activeFilters.activityFilters.excluded.length > 0))) ||
          (activeFilters.leadTimeRange && activeFilters.leadTimeRange.enabled &&
           (activeFilters.leadTimeRange.min !== null || activeFilters.leadTimeRange.max !== null)) ||
          (activeFilters.objectAttributePresenceFilters && activeFilters.objectAttributePresenceFilters.enabled &&
           activeFilters.objectAttributePresenceFilters.attributeNames?.length > 0) ||
          (activeFilters.variantFilter && activeFilters.variantFilter.enabled &&
           activeFilters.variantFilter.variantActivitiesList && activeFilters.variantFilter.variantActivitiesList.length > 0)
        );
        
        // Use appropriate endpoint based on whether filters are applied
        const apiPromise = hasFilters 
          ? api.post('/timeline-metrics', {
              start_date: startDate,
              end_date: endDate,
              time_granularity: granularity,
              object_type: objectType,
              metric
            }, {}, 'Timeline Metrics (Filtered)')
          : api.get('/timeline-metrics', {
              start_date: startDate,
              end_date: endDate,
              time_granularity: granularity,
              object_type: objectType,
              metric
            }, {}, 'Timeline Metrics (Unfiltered)');
        
        const res = await apiPromise;
        setTimelineData(res.data || []);
        
        console.log("📊 Timeline data loaded:", { 
          hasFilters, 
          dataPoints: res.data?.length || 0
        });
        
      } catch {
        setError("Failed to fetch timeline data.");
        setTimelineData([]);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [startDate, endDate, granularity, objectType, metric, api.currentFilters, activeFile]);

  return {
    objectTypes,
    timelineData,
    loading: loading || metadataLoading,
    error,
    isMetadataLoaded,
    fetchObjectTypes,
    globalDateRange,
    filters: {
      startDate,
      endDate,
      granularity,
      objectType,
    },
    setFilters: {
      setStartDate,
      setEndDate,
      setGranularity,
      setObjectType,
    },
  };
}